import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiBriefcase, FiSearch, FiDownload, FiPrinter, FiExternalLink } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { TRAN_TYPE_ROUTES, TRAN_TYPE_LABELS } from '../../utils/navigationHelper';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getJobsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

// TRAN_TYPE_ROUTES + TRAN_TYPE_LABELS come from the shared navigationHelper so job
// cost lines from EVERY type link (incl. MIP/PRC/PAY/DEP/FAD/CB the old map omitted).

const formatCurrency = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp }).format(amount);
};

const formatDate = (v) => fmtDate(v) || '-';

// Classify an account as revenue / expense (job P&L = account-type / COA driven).
// Prefer the stored accountType; fall back to the account code's leading digit
// (4xxxx = revenue, 5xxxx = expense). Everything else (assets, liabilities,
// equity — cash, party, inventory, GST) is balance-sheet, ignored for job P&L.
const classifyAccount = (accountType, code) => {
  const at = (accountType || '').toLowerCase();
  if (at.startsWith('revenue')) return 'revenue';
  if (at.startsWith('expens')) return 'expense';
  const c = (code || '').charAt(0);
  if (c === '4') return 'revenue';
  if (c === '5') return 'expense';
  return 'other';
};

const ZERO_SUMMARY = { revenue: 0, cost: 0, profit: 0, margin: 0, budget: 0, budgetUsedPct: 0 };

const JobCostSheet = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('job-cost-sheet');

  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(() => savedMem?.selectedJobId ?? '');
  const [accountsMap, setAccountsMap] = useState({});
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');

  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [costGroups, setCostGroups] = useState(() => savedMem?.costGroups ?? []);
  const [revenueGroups, setRevenueGroups] = useState(() => savedMem?.revenueGroups ?? []);
  const [summary, setSummary] = useState(() => savedMem?.summary ?? ZERO_SUMMARY);

  // ---- Loaders ----
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const snapshot = await getDocs(query(fyRef, orderBy('startDate', 'desc')));
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);
      if (!savedMem) {
        if (activeFinancialYear?.id) setSelectedFYId(activeFinancialYear.id);
        else if (fys.length > 0) setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  const loadJobs = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const jobRows = await getJobsCached(activeCompany.id);
      setJobs(jobRows.filter(r => r.jobCode != null)
        .sort((a, b) => String(a.jobCode || '').localeCompare(String(b.jobCode || ''))));
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  }, [activeCompany]);

  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const accountsRef = collection(db, 'companies', activeCompany.id, 'accounts');
      const snapshot = await getDocs(accountsRef);
      const map = {};
      snapshot.docs.forEach(d => {
        const a = d.data();
        map[d.id] = { code: a.code, name: a.name, accountType: a.accountType };
      });
      setAccountsMap(map);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadJobs();
    loadAccounts();
  }, [loadFinancialYears, loadJobs, loadAccounts]);

  const selectedJob = jobs.find(j => j.id === selectedJobId);

  // ---- Generate ----
  const generate = async () => {
    if (!activeCompany?.id) return;
    if (!selectedFYId) { alert('Please select a financial year'); return; }
    if (!selectedJobId) { alert('Please select a job'); return; }

    setLoading(true);
    setHasSearched(true);
    try {
      // 1. All transactions tagged to this job (single equality filter — no composite index needed)
      const txRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const txSnap = await getDocs(query(txRef, where('jobId', '==', selectedJobId)));
      // Keep ALL job transactions regardless of header status — invoices move to
      // Partial/Paid once settled, so a status==='Posted' filter would wrongly
      // drop them. The books-truth filter is applied at the subTransaction level
      // below (status==='Posted'); Draft docs carry Draft lines (excluded) and
      // Cancelled docs are hard-deleted (no lines).
      const txs = txSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => t.financialYearId === selectedFYId)
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return (t.branchId || null) === activeBranch.id;
        })
        .filter(t => {
          if (dateFrom && (t.docDate || '') < dateFrom) return false;
          if (dateTo && (t.docDate || '') > dateTo) return false;
          return true;
        });

      const txMap = {};
      txs.forEach(t => { txMap[t.id] = t; });
      const txIds = txs.map(t => t.id);

      if (txIds.length === 0) {
        const emptySummary = { ...ZERO_SUMMARY, budget: Number(selectedJob?.budgetAmount) || 0 };
        setCostGroups([]); setRevenueGroups([]);
        setSummary(emptySummary);
        saveMemory({ selectedFYId, selectedJobId, dateFrom, dateTo, costGroups: [], revenueGroups: [], summary: emptySummary, hasSearched: true });
        setLoading(false);
        return;
      }

      // 2. Their accounting lines (subTransactions), fetched in chunks of 10 by transactionId.
      //    jobId does NOT live on subTransactions, so we join back via transactionId.
      const subRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const subs = [];
      for (let i = 0; i < txIds.length; i += 10) {
        const chunk = txIds.slice(i, i + 10);
        const s = await getDocs(query(subRef, where('transactionId', 'in', chunk)));
        s.docs.forEach(d => subs.push({ id: d.id, ...d.data() }));
      }

      // 3. Classify each line by its account's COA type.
      const costMap = {};
      const revMap = {};
      subs.filter(s => s.status === 'Posted').forEach(s => {
        const acc = accountsMap[s.accountId];
        const code = acc?.code || s.accountCode;
        const cls = classifyAccount(acc?.accountType, code);
        if (cls !== 'revenue' && cls !== 'expense') return;
        const debit = s.debitAmount || 0;
        const credit = s.creditAmount || 0;
        const amount = cls === 'expense' ? debit - credit : credit - debit;
        const target = cls === 'expense' ? costMap : revMap;
        const key = s.accountId || code || 'unknown';
        if (!target[key]) {
          target[key] = { accountId: s.accountId, code: code || '', name: acc?.name || s.accountName || 'Unknown', total: 0, lines: [] };
        }
        target[key].total += amount;
        const tx = txMap[s.transactionId];
        target[key].lines.push({
          docDate: s.docDate || tx?.docDate || '',
          docNumber: s.docNumber || tx?.docNumber || '-',
          tranTypeCode: s.tranTypeCode || tx?.tranTypeCode || '',
          transactionId: s.transactionId,
          party: tx?.partyName || '',
          description: s.description || '',
          amount,
        });
      });

      const sortGroups = (m) => Object.values(m)
        .map(g => ({ ...g, lines: g.lines.sort((a, b) => (a.docDate || '').localeCompare(b.docDate || '')) }))
        .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

      const costList = sortGroups(costMap);
      const revList = sortGroups(revMap);
      const totalCost = costList.reduce((s, g) => s + g.total, 0);
      const totalRevenue = revList.reduce((s, g) => s + g.total, 0);
      const profit = totalRevenue - totalCost;
      const budget = Number(selectedJob?.budgetAmount) || 0;

      const summaryObj = {
        revenue: totalRevenue,
        cost: totalCost,
        profit,
        margin: totalRevenue !== 0 ? (profit / totalRevenue) * 100 : 0,
        budget,
        budgetUsedPct: budget > 0 ? (totalCost / budget) * 100 : 0,
      };
      setCostGroups(costList);
      setRevenueGroups(revList);
      setSummary(summaryObj);
      saveMemory({ selectedFYId, selectedJobId, dateFrom, dateTo, costGroups: costList, revenueGroups: revList, summary: summaryObj, hasSearched: true });
    } catch (error) {
      console.error('Error generating job cost sheet:', error);
      alert('Error generating report. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  const openTransaction = (tranTypeCode, transactionId) => {
    const route = TRAN_TYPE_ROUTES[tranTypeCode];
    if (route && transactionId) navigate(route, { state: { viewTransactionId: transactionId } });
  };

  // ---- Exports ----
  const handleExport = () => {
    if (!hasSearched) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    let csv = `Job Cost Sheet - ${selectedJob?.jobCode || ''} ${selectedJob?.jobName || ''}\n`;
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${fy?.yearName || fy?.yearCode || ''}\n`;
    if (dateFrom || dateTo) csv += `Period: ${dateFrom || 'start'} to ${dateTo || 'end'}\n`;
    csv += `\nREVENUE\nAccount,Date,Doc #,Type,Party,Description,Amount\n`;
    revenueGroups.forEach(g => {
      g.lines.forEach(l => {
        csv += `"${g.code} - ${g.name}",${formatDate(l.docDate)},${l.docNumber},${TRAN_TYPE_LABELS[l.tranTypeCode] || l.tranTypeCode},"${(l.party || '').replace(/"/g, '""')}","${(l.description || '').replace(/"/g, '""')}",${l.amount}\n`;
      });
      csv += `"${g.code} - ${g.name} Subtotal",,,,,,${g.total}\n`;
    });
    csv += `Total Revenue,,,,,,${summary.revenue}\n`;
    csv += `\nCOST\nAccount,Date,Doc #,Type,Party,Description,Amount\n`;
    costGroups.forEach(g => {
      g.lines.forEach(l => {
        csv += `"${g.code} - ${g.name}",${formatDate(l.docDate)},${l.docNumber},${TRAN_TYPE_LABELS[l.tranTypeCode] || l.tranTypeCode},"${(l.party || '').replace(/"/g, '""')}","${(l.description || '').replace(/"/g, '""')}",${l.amount}\n`;
      });
      csv += `"${g.code} - ${g.name} Subtotal",,,,,,${g.total}\n`;
    });
    csv += `Total Cost,,,,,,${summary.cost}\n`;
    csv += `\nBudget,,,,,,${summary.budget}\nProfit,,,,,,${summary.profit}\nMargin %,,,,,,${summary.margin.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `JobCostSheet_${selectedJob?.jobCode || 'job'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!hasSearched) return;
    const fy = financialYears.find(f => f.id === selectedFYId);
    const section = (title, groups, total, totalLabel) => `
      <h3>${title}</h3>
      <table>
        <thead><tr><th>Account</th><th>Date</th><th>Doc #</th><th>Type</th><th>Party / Description</th><th class="amount">Amount</th></tr></thead>
        <tbody>
          ${groups.map(g => `
            ${g.lines.map(l => `<tr>
              <td>${g.code} - ${g.name}</td>
              <td>${formatDate(l.docDate)}</td>
              <td>${l.docNumber}</td>
              <td>${TRAN_TYPE_LABELS[l.tranTypeCode] || l.tranTypeCode || '-'}</td>
              <td>${l.party || l.description || '-'}</td>
              <td class="amount">${formatCurrency(l.amount)}</td>
            </tr>`).join('')}
            <tr class="sub"><td colspan="5">${g.code} - ${g.name} — Subtotal</td><td class="amount">${formatCurrency(g.total)}</td></tr>
          `).join('')}
          <tr class="total"><td colspan="5">${totalLabel}</td><td class="amount">${formatCurrency(total)}</td></tr>
        </tbody>
      </table>`;

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Job Cost Sheet</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:13px}
        h1{color:#800020;font-size:20px;margin-bottom:4px}
        h3{color:#800020;font-size:15px;margin:22px 0 8px}
        .meta{color:#666;font-size:12px;margin-bottom:16px}
        .kpis{display:flex;flex-wrap:wrap;gap:20px;padding:14px;background:#f9f9f9;border-radius:8px;margin-bottom:10px}
        .kpi{text-align:center} .kpi .l{font-size:11px;color:#666} .kpi .v{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:8px}
        th{background:#800020;color:#fff;padding:8px;text-align:left;font-size:11px;text-transform:uppercase}
        td{padding:7px 8px;border-bottom:1px solid #eee}
        .amount{text-align:right;font-family:'Consolas',monospace}
        tr.sub td{background:#f5f5f5;font-weight:600}
        tr.total td{background:#efe6e9;font-weight:700}
        @media print{body{margin:15px}}
      </style></head><body>
      <h1>Job Cost Sheet</h1>
      <div class="meta">${activeCompany?.companyName || ''} | Job: ${selectedJob?.jobCode || ''} - ${selectedJob?.jobName || ''}${selectedJob?.customerName ? ' | Customer: ' + selectedJob.customerName : ''} | FY: ${fy?.yearName || fy?.yearCode || ''}${(dateFrom || dateTo) ? ' | Period: ' + (dateFrom || 'start') + ' to ' + (dateTo || 'end') : ''}</div>
      <div class="kpis">
        <div class="kpi"><div class="l">Budget</div><div class="v">${formatCurrency(summary.budget)}</div></div>
        <div class="kpi"><div class="l">Revenue</div><div class="v">${formatCurrency(summary.revenue)}</div></div>
        <div class="kpi"><div class="l">Cost</div><div class="v">${formatCurrency(summary.cost)}</div></div>
        <div class="kpi"><div class="l">Profit</div><div class="v">${formatCurrency(summary.profit)}</div></div>
        <div class="kpi"><div class="l">Margin</div><div class="v">${summary.margin.toFixed(1)}%</div></div>
        <div class="kpi"><div class="l">Budget Used</div><div class="v">${summary.budgetUsedPct.toFixed(1)}%</div></div>
      </div>
      ${section('Revenue', revenueGroups, summary.revenue, 'Total Revenue')}
      ${section('Cost', costGroups, summary.cost, 'Total Cost')}
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  const renderSection = (title, groups, totalLabel, total, isCost) => (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <Table>
        <Thead>
          <Tr>
            <Th>Account</Th>
            <Th>Date</Th>
            <Th>Doc #</Th>
            <Th>Type</Th>
            <Th>Party / Description</Th>
            <Th align="right">Amount</Th>
          </Tr>
        </Thead>
        <Tbody>
          {groups.length === 0 ? (
            <Tr><Td colSpan={6} style={{ textAlign: 'center', color: colors.text.tertiary }}>No {title.toLowerCase()} entries</Td></Tr>
          ) : groups.map(g => (
            <React.Fragment key={g.accountId || g.code}>
              {g.lines.map((l, i) => (
                <Tr key={`${g.accountId}-${i}`}>
                  <Td style={{ color: colors.text.secondary }}>{i === 0 ? `${g.code} - ${g.name}` : ''}</Td>
                  <Td>{formatDate(l.docDate)}</Td>
                  <Td>
                    {l.transactionId ? (
                      <DocNumber onClick={() => openTransaction(l.tranTypeCode, l.transactionId)} title="Open document">
                        {l.docNumber} <FiExternalLink size={11} />
                      </DocNumber>
                    ) : l.docNumber}
                  </Td>
                  <Td>{TRAN_TYPE_LABELS[l.tranTypeCode] || l.tranTypeCode || '-'}</Td>
                  <Td>{l.party || l.description || '-'}</Td>
                  <AmountCell align="right" $cost={isCost}>{formatCurrency(l.amount)}</AmountCell>
                </Tr>
              ))}
              <Tr $subtotal>
                <Td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>{g.code} - {g.name} — Subtotal</Td>
                <AmountCell align="right" $cost={isCost} style={{ fontWeight: 600 }}>{formatCurrency(g.total)}</AmountCell>
              </Tr>
            </React.Fragment>
          ))}
          <Tr $total>
            <Td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>{totalLabel}</Td>
            <AmountCell align="right" $cost={isCost} style={{ fontWeight: 700 }}>{formatCurrency(total)}</AmountCell>
          </Tr>
        </Tbody>
      </Table>
    </Section>
  );

  return (
    <PageLayout pageTitle="Job Cost Sheet" breadcrumb="Reports / Job Reports / Job Cost Sheet">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year *</Label>
            <Select value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>{fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup $span2>
            <Label>Job / Project *</Label>
            <Select value={selectedJobId} onChange={(e) => setSelectedJobId(e.target.value)}>
              <option value="">Select Job</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>{j.jobCode ? `${j.jobCode} - ` : ''}{j.jobName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>From Date</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>To Date</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={!hasSearched || loading}><FiDownload /> Export CSV</Button>
          <Button onClick={handlePrint} disabled={!hasSearched || loading}><FiPrinter /> Print</Button>
          <Button primary onClick={generate} disabled={loading}><FiSearch /> {loading ? 'Loading...' : 'Generate Report'}</Button>
        </ButtonGroup>
      </FiltersCard>

      <ContentCard>
        {!hasSearched ? (
          <EmptyState>
            <EmptyIcon><FiBriefcase /></EmptyIcon>
            <EmptyText>Select a financial year and job to view its cost sheet</EmptyText>
          </EmptyState>
        ) : loading ? (
          <EmptyState><EmptyText>Loading...</EmptyText></EmptyState>
        ) : (
          <>
            <SummaryHeader>
              <JobTitle>{selectedJob?.jobCode} - {selectedJob?.jobName}</JobTitle>
              {(selectedJob?.customerName || selectedJob?.status) && (
                <JobSubtitle>
                  {selectedJob?.customerName ? `Customer: ${selectedJob.customerName}` : ''}
                  {selectedJob?.status ? `${selectedJob?.customerName ? ' | ' : ''}Status: ${selectedJob.status}` : ''}
                </JobSubtitle>
              )}
              <KpiGrid>
                <Kpi><KpiLabel>Budget</KpiLabel><KpiValue>{formatCurrency(summary.budget)}</KpiValue></Kpi>
                <Kpi><KpiLabel>Revenue</KpiLabel><KpiValue $pos>{formatCurrency(summary.revenue)}</KpiValue></Kpi>
                <Kpi><KpiLabel>Cost</KpiLabel><KpiValue $neg>{formatCurrency(summary.cost)}</KpiValue></Kpi>
                <Kpi><KpiLabel>Profit</KpiLabel><KpiValue $pos={summary.profit >= 0} $neg={summary.profit < 0}>{formatCurrency(summary.profit)}</KpiValue></Kpi>
                <Kpi><KpiLabel>Margin</KpiLabel><KpiValue>{summary.margin.toFixed(1)}%</KpiValue></Kpi>
                <Kpi><KpiLabel>Budget Used</KpiLabel><KpiValue>{summary.budget > 0 ? `${summary.budgetUsedPct.toFixed(1)}%` : '-'}</KpiValue></Kpi>
              </KpiGrid>
            </SummaryHeader>

            <TableContainer>
              {renderSection('Revenue', revenueGroups, 'Total Revenue', summary.revenue, false)}
              {renderSection('Cost', costGroups, 'Total Cost', summary.cost, true)}
            </TableContainer>
          </>
        )}
      </ContentCard>
    </PageLayout>
  );
};

// ---- Styled Components ----
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;
const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;
const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.$span2 && 'grid-column: span 2;'}
`;
const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;
const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;
const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[200]}; }
  `}
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;
const SummaryHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;
const JobTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 4px 0;
`;
const JobSubtitle = styled.p`
  font-size: 13px;
  color: ${colors.text.secondary};
  margin: 0 0 12px 0;
`;
const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-top: 8px;
`;
const Kpi = styled.div`
  background: white;
  padding: 14px 16px;
  border-radius: ${colors.borderRadius.md};
  border: 1px solid ${colors.neutral[200]};
`;
const KpiLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;
const KpiValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.$pos ? '#2E7D32' : props.$neg ? '#C62828' : colors.text.primary};
`;
const TableContainer = styled.div`
  overflow-x: auto;
`;
const Section = styled.div`
  padding: 8px 20px 16px;
`;
const SectionTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.primary.main};
  margin: 16px 0 10px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;
const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;
const Th = styled.th`
  padding: 10px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;
const Tbody = styled.tbody``;
const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  &:hover { background: ${colors.neutral[50]}; }
  ${props => props.$subtotal && `background: ${colors.neutral[50]};`}
  ${props => props.$total && `background: ${colors.primary.main}0D; font-weight: 600;`}
`;
const Td = styled.td`
  padding: 9px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;
const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
  text-align: right;
  color: ${props => props.$cost ? '#C62828' : '#2E7D32'};
`;
const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { text-decoration: underline; }
  svg { opacity: 0.6; }
`;
const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;
const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;
const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default JobCostSheet;
